/**
 * Хук для Socket.IO real-time інвалідації квестів.
 *
 * Слухає "quests:invalidate" → re-fetch квестів через існуючий HTTP ендпоінт.
 * При reconnect також тригерить re-fetch.
 * Debounce 300ms щоб не посилати надмірні HTTP-запити при burst сигналах.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { connectSocket, getSocket } from "../socket";
import { prefetchAllQuests } from "../utils/quest/questsCache";

export function useQuestSocket(): void {
  const queryClient = useQueryClient();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // queryClient is stable for the QueryClientProvider lifetime; listeners re-bind only if it changes.
  useEffect(() => {
    const socket = getSocket();

    const triggerRefetch = () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        prefetchAllQuests(queryClient).catch((err) => {
          console.warn("[useQuestSocket] Failed to prefetch quests:", err);
        });
      }, 300);
    };

    const onInvalidate = () => {
      triggerRefetch();
    };

    const onConnect = () => {
      // При (ре)підключенні завжди синхронізуємо квести
      triggerRefetch();
    };

    socket.on("quests:invalidate", onInvalidate);
    socket.on("connect", onConnect);
    socket.on("reconnect", onConnect);

    connectSocket();

    return () => {
      socket.off("quests:invalidate", onInvalidate);
      socket.off("connect", onConnect);
      socket.off("reconnect", onConnect);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [queryClient]);
}
